import { useCallback } from 'react';

import { useIntl } from 'react-intl-next';
import { createHook, createStore } from 'react-sweet-state';

export const PROMPT_PLACEHOLDER_CHAR = '`';

import messages from './messages';

type PlaceholderType = 'person' | 'link' | 'generic' | 'highlight-action';

type PlaceholderData = {
	prompt: string;
	type: PlaceholderType;
	selectionStartIndex: number;
	selectionEndIndex: number;
};

type State = {
	placeholder: PlaceholderData | null;
};

const state = createStore({
	initialState: {
		placeholder: null,
	} as State,
	actions: {
		setPlaceholder:
			(placeholder: State['placeholder']) =>
			({ setState }) => {
				setState({ placeholder });
			},
	},
});

// exported for testing
export const usePlaceholderStore = createHook(state);

const getPlaceholderType = (placeholderLabel: string): PlaceholderType => {
	switch (placeholderLabel) {
		case 'person':
			return 'person';
		case 'highlight-action':
			return 'highlight-action';
		default:
			return 'link';
	}
};

const isPromptWithPlaceholder = (prompt: string) => {
	return prompt.includes(PROMPT_PLACEHOLDER_CHAR);
};

const getHighlightActionPlaceholderData = (prompt: string, placeholderText: string) => {
	const formattedPrompt = `${prompt}\n\n${placeholderText}`;
	const startIndexOfPlaceholder = formattedPrompt.length - placeholderText.length;
	const endIndexOfPlaceholder = formattedPrompt.length + 1;
	const placeholderLabel = 'highlight-action';
	const selectionStartIndex = startIndexOfPlaceholder + 1;
	const selectionEndIndex = endIndexOfPlaceholder;
	return {
		prompt: formattedPrompt,
		type: getPlaceholderType(placeholderLabel),
		selectionStartIndex,
		selectionEndIndex,
	};
};

const getDefaultPlaceholderData = (prompt: string) => {
	const startIndexOfPlaceholder = prompt.indexOf(PROMPT_PLACEHOLDER_CHAR);
	const endIndexOfPlaceholder = prompt.lastIndexOf(PROMPT_PLACEHOLDER_CHAR);
	const placeholderLabel = prompt.slice(startIndexOfPlaceholder + 1, endIndexOfPlaceholder);
	const selectionStartIndex = startIndexOfPlaceholder + 1;
	const selectionEndIndex = endIndexOfPlaceholder;
	const promptWithoutBackticks = prompt.replace(new RegExp(PROMPT_PLACEHOLDER_CHAR, 'g'), '');
	return {
		prompt: promptWithoutBackticks,
		type: getPlaceholderType(placeholderLabel),
		selectionStartIndex,
		selectionEndIndex,
	};
};

export const useEditorPlaceholder = () => {
	const [{ placeholder }, { setPlaceholder }] = usePlaceholderStore();
	const { formatMessage } = useIntl();

	const insertPlaceholder = useCallback(
		(prompt: string, overrideType?: PlaceholderType) => {
			let placeholderData: PlaceholderData;

			if (overrideType === 'highlight-action') {
				const highlightActionsPlaceholderPrompt = formatMessage(
					messages.highlightActionsPlaceholderPrompt,
				);
				placeholderData = getHighlightActionPlaceholderData(
					prompt,
					highlightActionsPlaceholderPrompt,
				);
			} else {
				placeholderData = getDefaultPlaceholderData(prompt);
			}

			setPlaceholder({
				prompt: placeholderData.prompt,
				type: placeholderData.type,
				selectionStartIndex: placeholderData.selectionStartIndex,
				selectionEndIndex: placeholderData.selectionEndIndex,
			});
		},
		[formatMessage, setPlaceholder],
	);

	const clearPlaceholder = useCallback(() => {
		setPlaceholder(null);
	}, [setPlaceholder]);

	return {
		insertPlaceholder,
		clearPlaceholder,
		placeholder,
		isPromptWithPlaceholder,
	};
};
